import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EleveGroup } from '@teetsh/app/src/types/eleveGroups';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { Color } from 'types/colors';
import { ELEVE_GROUPS_KEY } from './useEleveGroups';
import { EPHEMERAL_GROUPS_KEY } from './useEphemeralEleveGroups';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';

interface CreateEleveGroupParams {
  name: string;
  color: Color | TagColorWithIntensity;
  elevesIds: string[];
  isEphemeral?: boolean;
}

export function useCreateEleveGroup() {
  const queryClient = useQueryClient();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['eleve']);

  return useMutation({
    mutationFn: ({
      name,
      color,
      elevesIds,
      isEphemeral,
    }: CreateEleveGroupParams) =>
      request(
        `/schoolyears/${schoolyear.id}/schools/${school.id}/eleve-groups`,
        {
          method: 'POST',
          body: { name, color, elevesIds, isEphemeral },
        },
      ).then<EleveGroup>(data => data),
    onSuccess: () => {
      // Invalidate regular groups
      queryClient.invalidateQueries({
        queryKey: [schoolyear.id, school.id, ELEVE_GROUPS_KEY],
      });
      // Also invalidate all ephemeral groups queries
      queryClient.invalidateQueries({
        queryKey: [schoolyear.id, school.id, EPHEMERAL_GROUPS_KEY],
      });
    },
    onError: () => {
      notifyError({
        title: t('eleve:messages.createGroupError'),
      });
    },
  });
}
